import { t } from 'i18next'
import React, { FC, useCallback, useState } from 'react'
import { Control, Controller, useFieldArray, useFormContext } from 'react-hook-form'
import { Trans } from 'react-i18next'
import { v4 } from 'uuid'

import { Add, Delete } from '@mui/icons-material'
import { Box, Divider, Grid, InputAdornment, Paper, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { ActionButton } from '../../../../components/ActionButton/ActionButton'
import { ErrorStatusCircle } from '../../../../components/ErrorStatusCircle/ErrorStatusCircle'
import { isEmpty } from '../../../../helpers/isEmpty'
import { Currency } from '../../../../types/Currency'
import { MedianDisplay } from './MedianDisplay'
import './PricingDataFlexContainer.scss'

type CurrencyDisplayProps = {
  currencies: Currency[] | undefined
  onChange: (currencies: Currency[]) => void
}

type removeCurrencyDialogData = {
  name: string
}

export const CurrencyDisplay: FC<CurrencyDisplayProps> = ({ currencies, onChange }) => {
  const { control, watch } = useFormContext<{ currencies: Currency[] }>()
  const formCurrencies = watch('currencies')
  const [removeCurrencyDialogData, setRemoveCurrencyDialogData] = useState<ConfirmDialogData<removeCurrencyDialogData>>()
  const removeCurrency = useCallback((name: string) => {
    setRemoveCurrencyDialogData({
      title: t('internal-live-events:remove_currency_confirm_dialog_title'),
      confirmText: t('internal-live-events:remove_currency_confirm_dialog_confirm_text'),
      actionText: t('internal-common:yes'),
      cancelText: t('internal-common:no'),
      destructiveAction: true,
      data: { name },
    })
  }, [])

  const handleRemoveCurrencyConfirm = (dialogData?: ConfirmDialogData<removeCurrencyDialogData>) => {
    if (dialogData?.data) {
      if (currencies) {
        const newCurrencyList = currencies.filter((currency) => currency.name !== dialogData.data?.name)
        onChange(newCurrencyList)
      }
    }

    setRemoveCurrencyDialogData(undefined)
  }

  const isUniqueCurrencyName = (name: string) => {
    const duplicates = formCurrencies.filter((currency) => currency.name.trimEnd() === name.trimEnd())
    return duplicates.length < 2 ? true : false
  }

  return (
    <>
      {currencies?.length || -1 > 0 ? (
        <Grid item xs={12}>
          <Typography variant="h3">
            <Trans i18nKey="internal-live-events:currencies" />
          </Typography>
        </Grid>
      ) : null}

      {formCurrencies.map((currency, index) => {
        return (
          <Paper key={currency.id || v4()} elevation={2} sx={{ p: 3, mt: 2, width: '100%' }}>
            <Grid item sm={12}>
              <Controller
                name={`currencies.${index}.name`}
                control={control}
                rules={{ required: true, validate: isUniqueCurrencyName }}
                render={({ field }) => {
                  return (
                    <TextField
                      required
                      type="text"
                      value={field.value}
                      onChange={field.onChange}
                      variant="outlined"
                      size="small"
                      label={t('internal-live-events:currency_name')}
                      fullWidth
                      error={!isUniqueCurrencyName(field.value) || isEmpty(field.value)}
                      InputProps={{
                        endAdornment:
                          !isUniqueCurrencyName(field.value) || isEmpty(field.value) ? (
                            <InputAdornment position="end">
                              <ErrorStatusCircle
                                errorText={
                                  !isUniqueCurrencyName(field.value)
                                    ? t('internal-live-events:duplicate_currency_error_message')
                                    : t('internal-live-events:invalid_input')
                                }
                              />
                            </InputAdornment>
                          ) : null,
                      }}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item sm={12} sx={{ mt: 2 }}>
              <Controller
                name={`currencies.${index}.currencyDescription`}
                control={control}
                render={({ field }) => {
                  return (
                    <TextField
                      type="text"
                      value={field.value || ''}
                      onChange={field.onChange}
                      variant="outlined"
                      size="small"
                      label={t('live-events:use')}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  )
                }}
              />
            </Grid>
            <Grid item sm={12} sx={{ mt: 2 }}>
              <Controller
                name={`currencies.${index}.purposeDescription`}
                control={control}
                render={({ field }) => {
                  return (
                    <TextField
                      type="text"
                      value={field.value || ''}
                      onChange={field.onChange}
                      variant="outlined"
                      size="small"
                      label={t('live-events:how_to_get')}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  )
                }}
              />
            </Grid>
            <Divider sx={{ width: '100%', mt: 2 }} />

            <BundleCurrencies control={control} index={index} t={t} />
            {currency.values && currency.values.length > 0 && (
              <Grid item xs={12}>
                <MedianDisplay currency={currency} t={t} />
              </Grid>
            )}

            <Box sx={{ mt: 2, display: 'flex' }}>
              <ActionButton color="warning" onClick={() => removeCurrency(currency.name)}>
                <Delete fontSize="small" />
                <Typography>{t('internal-live-events:remove_currency_confirm_dialog_title')}</Typography>
              </ActionButton>
            </Box>
          </Paper>
        )
      })}

      <ConfirmDialog open={!!removeCurrencyDialogData} onClose={handleRemoveCurrencyConfirm} confirmDialogData={removeCurrencyDialogData} />
    </>
  )
}

type BundleProps = {
  control: Control<{ currencies: Currency[] }>
  index: number
  t: (key: string) => string
}

export const BundleCurrencies: React.FC<BundleProps> = ({ control, index, t }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `currencies.${index}.values` as const,
  })

  const addRow = () => append({ usd: '' as any, value: '' as any })

  const isValidate = (value: string | number | undefined) => {
    if (!value || value === '' || (typeof value === 'string' && value.trim() === '')) return true
    if (isNaN(Number(value))) return true
    if (Number(value) <= 0) return true // Rejects 0, 0.00, negative numbers
    return false
  }

  return (
    <React.Fragment>
      {fields.map((pair, i) => (
        <Grid container spacing={2} sx={{ ml: -2, mt: 0.5 }} key={pair.id}>
          <Grid item sm={4}>
            <Controller
              name={`currencies.${index}.values.${i}.usd`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const hasError = isValidate(field.value)
                return (
                  <TextField
                    {...field}
                    required
                    type="text"
                    size="small"
                    label={t('internal-live-events:usd')}
                    fullWidth
                    error={hasError}
                    InputProps={{
                      endAdornment: hasError ? (
                        <InputAdornment position="end">
                          <ErrorStatusCircle errorText={t('internal-live-events:enter_number_error_message')} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Controller
              name={`currencies.${index}.values.${i}.value`}
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                const hasError = isValidate(field.value)
                return (
                  <TextField
                    {...field}
                    required
                    type="text"
                    size="small"
                    label={t('internal-live-events:in_game_currency')}
                    fullWidth
                    error={hasError}
                    InputProps={{
                      endAdornment: hasError ? (
                        <InputAdornment position="end">
                          <ErrorStatusCircle errorText={t('internal-live-events:enter_number_error_message')} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                )
              }}
            />
          </Grid>
          <Grid item sm={4}>
            <Box sx={{ display: 'inline-flex', ml: 2, mt: 0.5 }}>
              <Box sx={{ mr: 1 }}>
                <ActionButton onClick={addRow}>
                  <Add fontSize="small" />
                  <Typography>{t('internal-live-events:add_more')}</Typography>
                </ActionButton>
              </Box>
              {fields.length > 1 && (
                <Box>
                  <ActionButton onClick={() => remove(i)} color="warning">
                    <Delete fontSize="small" />
                    <Typography>{t('common:remove')}</Typography>
                  </ActionButton>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      ))}
      {fields.length === 0 && (
        <ActionButton onClick={addRow} sx={{ mt: 2 }}>
          <Add fontSize="small" />
          <Typography>{t('internal-live-events:add_more')}</Typography>
        </ActionButton>
      )}
    </React.Fragment>
  )
}
